import {format, subMonths} from 'date-fns';
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {QueryParamsEnum} from 'presentation/constants';
import {useDebounce} from 'presentation/hooks';
import {useQueryParams} from 'presentation/pages/private/hooks';

import {DEFAULT_CONSULT_REFUND_V2_FILTERS} from '../components/ConsultRefundFilterDrawer/filterConstants';
import {ConsultRefundV2ContextValue} from './types';

const ConsultRefundV2Context = createContext<ConsultRefundV2ContextValue | null>(null);

function defaultDateRange() {
	const today = new Date();
	return {
		end: format(today, 'yyyy-MM-dd'),
		start: format(subMonths(today, 3), 'yyyy-MM-dd'),
	};
}

export function ConsultRefundV2Provider({children}: {children: React.ReactNode}) {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const latestQueryRef = useRef(query);
	const [consultInitialDate, setConsultInitialDate] = useState(() => defaultDateRange().start);
	const [consultEndDate, setConsultEndDate] = useState(() => defaultDateRange().end);
	const [consultProtocolNumber, setConsultProtocolNumber] = useState(() => (query || '').trim());
	const [filterStatus, setFilterStatus] = useState(DEFAULT_CONSULT_REFUND_V2_FILTERS.status);
	const [filterModality, setFilterModality] = useState(
		DEFAULT_CONSULT_REFUND_V2_FILTERS.modality,
	);
	const [datePickerOpenRequest, setDatePickerOpenRequest] = useState(0);

	function setConsultRefundFilters(filters: {modality: string; status: string}) {
		setFilterStatus(filters.status);
		setFilterModality(filters.modality);
	}

	function clearConsultRefundFilters() {
		setFilterStatus(DEFAULT_CONSULT_REFUND_V2_FILTERS.status);
		setFilterModality(DEFAULT_CONSULT_REFUND_V2_FILTERS.modality);
	}

	const [debouncedQueryFunc, abortDebounce] = useDebounce(() => {
		setConsultProtocolNumber(latestQueryRef.current.trim());
		clearConsultRefundFilters();
	}, 500);

	useEffect(() => {
		latestQueryRef.current = query;
	}, [query]);

	function clearSearchParams() {
		abortDebounce();
		setConsultProtocolNumber('');
		clearConsultRefundFilters();
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				return prev;
			},
			{replace: true},
		);
	}

	function setDateRange(range: {end: string; start: string}) {
		setConsultInitialDate(range.start);
		setConsultEndDate(range.end);
		clearConsultRefundFilters();
	}

	function requestOpenDatePicker() {
		setDatePickerOpenRequest(n => n + 1);
	}

	const activeRefundFilterCount =
		(filterStatus !== DEFAULT_CONSULT_REFUND_V2_FILTERS.status ? 1 : 0) +
		(filterModality !== DEFAULT_CONSULT_REFUND_V2_FILTERS.modality ? 1 : 0);

	const value: ConsultRefundV2ContextValue = useMemo(
		() => ({
			abortDebounce,
			activeRefundFilterCount,
			clearConsultRefundFilters,
			clearSearchParams,
			consultEndDate,
			consultInitialDate,
			consultProtocolNumber,
			datePickerOpenRequest,
			debouncedQueryFunc,
			filterModality,
			filterStatus,
			requestOpenDatePicker,
			setConsultRefundFilters,
			setDateRange,
		}),
		[
			abortDebounce,
			activeRefundFilterCount,
			clearConsultRefundFilters,
			clearSearchParams,
			consultEndDate,
			consultInitialDate,
			consultProtocolNumber,
			datePickerOpenRequest,
			debouncedQueryFunc,
			filterModality,
			filterStatus,
			requestOpenDatePicker,
			setConsultRefundFilters,
			setDateRange,
		],
	);

	return (
		<ConsultRefundV2Context.Provider value={value}>{children}</ConsultRefundV2Context.Provider>
	);
}

export function useConsultRefundContext(): ConsultRefundV2ContextValue {
	const context = useContext(ConsultRefundV2Context);
	if (!context) {
		throw new Error('useConsultRefundContext must be used within ConsultRefundV2Provider');
	}
	return context;
}
